import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "../../utils";
import { logger } from "@/lib/logger";
import type { Employee_Contact_AccessInsertInput } from "@/src/gql/graphql";
import type { EmployeeAccessRecord } from "@/components/admin/grant-employee-access/types";

export function useEmployeeClientAccess(userId: string) {
  return useQuery({
    queryKey: ["admin", "users", userId, "employee-client-access"],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("employee_contact_access")
        .select("*, contact:contacts(*)")
        .eq("user_id", userId);

      if (error) {
        logger.error("Error fetching employee client access", error);
        throw new Error(error.message);
      }

      return data || [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGrantEmployeeAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, contactId }: { userId: string; contactId: string }) => {
      const supabase = getSupabase();
      // Strongly-typed insert — employee_contact_access has NO employee_id and
      // NO granted_at column; the verified columns are user_id, contact_id,
      // granted_by, created_at. (granted_by/created_at are DB-defaulted.)
      const insertPayload: Employee_Contact_AccessInsertInput = {
        user_id: userId,
        contact_id: contactId,
      };
      const { data, error } = await supabase
        .from("employee_contact_access")
        .insert([insertPayload]);
      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "users", variables.userId, "employee-client-access"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "contacts", variables.contactId, "employee-access"],
      });
      toast.success("Employee access granted successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to grant employee access");
      logger.error("Error granting employee access", error);
    },
  });
}

export function useRevokeEmployeeAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, contactId }: { userId: string; contactId: string }) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("employee_contact_access")
        .delete()
        .match({ user_id: userId, contact_id: contactId });
      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "users", variables.userId, "employee-client-access"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "contacts", variables.contactId, "employee-access"],
      });
      toast.success("Employee access revoked successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to revoke employee access");
      logger.error("Error revoking employee access", error);
    },
  });
}

export function useContactEmployeeAccess(contactId: string | null | undefined) {
  return useQuery({
    queryKey: ["admin", "contacts", contactId, "employee-access"],
    queryFn: async () => {
      if (!contactId) return [];

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("employee_contact_access")
        .select(
          `
          *,
          user:app_users!user_id (
            id,
            email,
            first_name,
            last_name,
            is_active
          )
        `
        )
        .eq("contact_id", contactId);

      if (error) {
        logger.error("Error fetching contact employee access", error);
        throw new Error(error.message);
      }

      type RawUser = {
        id: string;
        email: string | null;
        first_name: string | null;
        last_name: string | null;
        is_active: boolean | null;
      } | null;
      type RawRow = {
        id: string;
        user_id: string;
        contact_id: string;
        created_at: string | null;
        user: RawUser;
      };
      // Map to the shared EmployeeAccessRecord shape (user optional, email/name
      // strings) so consumers (GrantEmployeeAccessForm's access list) align.
      return ((data ?? []) as unknown as RawRow[]).map(
        (item): EmployeeAccessRecord => ({
          id: item.id,
          userId: item.user_id,
          contactId: item.contact_id,
          user: item.user
            ? {
                id: item.user.id,
                email: item.user.email ?? "",
                first_name: item.user.first_name ?? undefined,
                last_name: item.user.last_name ?? undefined,
              }
            : undefined,
        })
      );
    },
    enabled: !!contactId,
    staleTime: 5 * 60 * 1000,
  });
}
